import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule } from 'angular-i18next';
import { AumicoComponentsModule } from '../../../aumico-components/src/lib';
import { OrganizationProvider } from '../public-api';
import { ActivateUserComponent } from './components/activate-user/activate-user.component';
import { AddEditChecklistComponent } from './components/add-edit-checklist/add-edit-checklist.component';
import { ChecklistFormComponent } from './components/add-edit-checklist/checklist-form/checklist-form.component';
import { AppRoleFilterDropDownComponent } from './components/approle-filter-drop-down/approle-filter-drop-down.component';
import { ChangeFCPStateComponent } from './components/change-fcpstate/change-fcpstate.component';
import { ChecklistTableNewComponent } from './components/checklist-table-new/checklist-table-new.component';
import { ChecklistTableComponent } from './components/checklist-table/checklist-table.component';
import { CountryFilterDropDownComponent } from './components/country-filter-drop-down/country-filter-drop-down.component';
import { EditUserOrganizationGeneralInformationComponent } from './components/edit-user-organization/general-information/edit-user-organization-general-information.component';
import { EditUserGeneralInformationComponent } from './components/edit-user/general-information/general-information.component';
import { EditUserSecurityComponent } from './components/edit-user/security/security.component';
import { CreateOrUpdateLicencePackageComponent } from './components/licence-package/create-licence-package.component';
import { PasswordsComponent } from './components/passwords/passwords.component';
import { PersonalInformationComponent } from './components/personal-information/personal-information.component';
import { PolicyAndSubscriptionControlsComponent } from './components/policy-and-subscription-controls/policy-and-subscription-controls.component';
import { RegistrationComponent } from './components/registration/registration.component';
import { OrganizationRoleFilterDropDownComponent } from './components/role-filter-drop-down/role-filter-drop-down.component';
import { ShowOrganizationPackagesComponent } from './components/show-organization-packages/show-organization-packages.component';
import { StateFilterDropDownComponent } from './components/state-filter-drop-down/state-filter-drop-down.component';
import { TierFilterDropDownComponent } from './components/tier-filter-drop-down/tier-filter-drop-down.component';
import { UpdateOrganizationModalComponent } from './components/update-organization-modal/update-organization-modal.component';
import { UploadOrganizationPdfTemplateComponent } from './components/upload-organization-pdf-template/upload-organization-pdf-template.component';
import { UserModificationConfirmationModal } from './components/user-modification-confirmation-modal/user-modification-confirmation-modal.component';
import { AuthEffects } from './effects/auth.effects';
import { OrganizationCheckListEffects } from './effects/checklist.effects';
import { InvitationEffects } from './effects/invitation.effects';
import { LicenceEffects } from './effects/licence.effects';
import { OrganizationEffects } from './effects/organization.effects';
import { SsoEffects } from './effects/sso.effects';
import { UserEffects } from './effects/user.effects';
import { UsersManagementEffects } from './effects/users.management.effects';
import { AcceptInvitationPage } from './pages/accept-invitation/accept-invitation.page';
import { DesignSettingsPage } from './pages/design-settings/design-settings.page';
import { OrganizationGeneralInformationPage } from './pages/organization-general-information/organization-general-information.page';
import { OrganizationSettingsPage } from './pages/organization-settings/organization-settings.page';
import { InviteUserComponent } from './pages/organization-users/invite-user/invite-user.component';
import { OrganizationUsersPage } from './pages/organization-users/organization-users.page';
import { ResendInvitationComponent } from './pages/organization-users/resend-invitation/resend-invitation.component';
import { WithdrawInvitationComponent } from './pages/organization-users/withdraw-invitation/withdraw-invitation.component';
import { OrganizationsListComponent } from './pages/organizations-list/organizations-list.component';
import { ResetPasswordPage } from './pages/reset-password/reset-password.page';
import { QuickSignUpPage } from './pages/sign-up/quick-sign-up.page';
import { SignUpPage } from './pages/sign-up/sign-up.page';
import { UserGeneralInformationPage } from './pages/user-general-information/user-general-information.page';
import { UserSecurityPage } from './pages/user-security/user-security.page';
import { UsersListPage } from './pages/users-list/users-list.page';
import { authFeature } from './reducers/auth.reducer';
import { defaultChecklistFeature } from './reducers/default-checklist.reducer';
import { invitationFeature } from './reducers/invitation.reducer';
import { organizationChecklistFeature } from './reducers/organization-checklist.reducer';
import * as fromOrganizationReducer from './reducers/organization.reducer';
import { ssoFeature } from './reducers/sso.reducer';
import * as fromManagementReducer from './reducers/users.management.reducer';
import { AuthService } from './services/auth.service';
import { LicenceService } from './services/licence.service';
import { OrganizationService } from './services/organization.service';
import { SsoService } from './services/sso.service';
import { UserService } from './services/user.service';
import { UsersManagementService } from './services/users.management.service';
import { UsersConfig, UsersConfigService } from './users.config';

const EXPORTED_DECLARATIONS = [
    EditUserGeneralInformationComponent,
    EditUserSecurityComponent,
    EditUserOrganizationGeneralInformationComponent,
    ActivateUserComponent,
    PersonalInformationComponent,
    RegistrationComponent,
    PasswordsComponent,
    UserGeneralInformationPage,
    UserSecurityPage,
    OrganizationGeneralInformationPage,
    UsersListPage,
    SignUpPage,
    QuickSignUpPage,
    ResetPasswordPage,
    OrganizationUsersPage,
    UserModificationConfirmationModal,
    AcceptInvitationPage,
    PolicyAndSubscriptionControlsComponent,
    OrganizationsListComponent,
    OrganizationSettingsPage,
    DesignSettingsPage,
    UploadOrganizationPdfTemplateComponent,
    AddEditChecklistComponent,
    ChecklistTableNewComponent,
    ChecklistFormComponent,
    ChecklistTableComponent,
    InviteUserComponent,
    OrganizationRoleFilterDropDownComponent,
    AppRoleFilterDropDownComponent,
    TierFilterDropDownComponent,
    CountryFilterDropDownComponent,
    StateFilterDropDownComponent,
];
const ANGULAR_MATERIAL = [MatGridListModule, MatTooltipModule, MatSelectModule];

@NgModule({
    declarations: [
        EXPORTED_DECLARATIONS,
        OrganizationsListComponent,
        UpdateOrganizationModalComponent,
        ChangeFCPStateComponent,
        CreateOrUpdateLicencePackageComponent,
        ShowOrganizationPackagesComponent,
        WithdrawInvitationComponent,
        ResendInvitationComponent,
    ],
    providers: [OrganizationProvider, OrganizationService],
    imports: [
        CommonModule,
        I18NextModule,
        StoreModule.forFeature(authFeature),
        StoreModule.forFeature(invitationFeature),
        StoreModule.forFeature(defaultChecklistFeature),
        StoreModule.forFeature(organizationChecklistFeature),
        StoreModule.forFeature(fromOrganizationReducer.organizationFeatureKey, fromOrganizationReducer.reducer),
        StoreModule.forFeature(fromManagementReducer.usersMngFeatureKey, fromManagementReducer.reducer),
        StoreModule.forFeature(ssoFeature.name, ssoFeature.reducer),

        EffectsModule.forFeature([
            UsersManagementEffects,
            AuthEffects,
            OrganizationEffects,
            InvitationEffects,
            OrganizationCheckListEffects,
            LicenceEffects,
            SsoEffects,
            UserEffects,
        ]),
        MatTabsModule,
        AumicoCoreModule,
        MatDatepickerModule,
        AumicoComponentsModule,
        FormsModule,
        ReactiveFormsModule,
        ANGULAR_MATERIAL,
        MatIconModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatButtonModule,
        MatDialogModule,
        MatPasswordStrengthModule,
        MatInputModule,
        MatCardModule,
        MatListModule,
        ReactiveFormsModule,
    ],
    exports: [EXPORTED_DECLARATIONS],
})
export class UsersModule {
    static forRoot(config: UsersConfig): ModuleWithProviders<UsersModule> {
        return {
            ngModule: UsersModule,
            providers: [
                UsersManagementService,
                AuthService,
                OrganizationService,
                LicenceService,
                SsoService,
                UserService,
                {
                    provide: UsersConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
